/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, IObjectWithKey, ISelection } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { AllFilesViewCommandBar } from 'src/customText/appShell/dataTaggingPage/components/allFilesView/AllFilesViewCommandBar';
import { AllFilesViewTable } from 'src/customText/appShell/dataTaggingPage/components/allFilesView/AllFilesViewTable';
import { CtFiltersPillContainer } from 'src/customText/appShell/dataTaggingPage/components/CtFiltersPillContainer';
import { FileView } from 'src/customText/appShell/dataTaggingPage/components/FilesViewDropdown';
import { SamplingFooter } from 'src/customText/appShell/dataTaggingPage/components/SamplingFooter';
import { SavingState } from 'src/customText/appShell/dataTaggingPage/hooks/useSaveChanges';
import { DocumentsFilter } from 'src/customText/appShell/dataTaggingPage/samplerStore';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';

const messages = defineMessages({
    emptyStateMsg: {
        id: 'customText.allFilesView.emptyStateMsg',
        defaultMessage: `You don't any documents in your storage.`
    }
});

type AllFilesViewProps = {
    query: string;
    savingState: SavingState;
    appliedFilters: DocumentsFilter;
    documents: CustomTextDocumentMetadata[];
    selection: ISelection<IObjectWithKey>;
    isLoadingMoreFiles: boolean;
    showLoadMoreFiles: boolean;
    isExtractionProject: boolean;
    onSave: () => void;
    setQuery: (query: string) => void;
    onDocumentClick: (name: string) => void;
    onFileViewChange: (fileView: FileView) => void;
    onChangeFilter: (filter: DocumentsFilter) => void;
    onRenderWarnings?: () => JSX.Element;
    handleLoadMoreFiles: () => void;
};

const { styleDiv } = getStylistV2('AllFilesView');
const DocumentsTableWindow = styleDiv('DocumentsTableWindow', (_, cssVars) => ({ height: cssVars.height, overflowY: 'auto', flex: 1 }));
const containerStyles = classNamesFunction<IStackProps, IStackStyles>()({ root: { minHeight: 500 } });
const paginationContainerStyles = classNamesFunction<IStackProps, IStackStyles>()({
    root: {
        userSelect: 'none',
        marginTop: `4 !important`,
        marginBottom: `4 !important`
    }
});

/**
 * A component rendering a list of all available documents
 * in the tagging data page.
 */
export const AllFilesView = observer((props: AllFilesViewProps) => {
    const {
        query,
        documents,
        selection,
        savingState,
        appliedFilters,
        showLoadMoreFiles,
        isLoadingMoreFiles,
        isExtractionProject,
        onSave,
        setQuery,
        onChangeFilter,
        onDocumentClick,
        onFileViewChange,
        onRenderWarnings,
        handleLoadMoreFiles
    } = props;

    const { rootStore } = useGlobalState();
    const { isAccessibleMode } = useZoomLevel();
    const { localize } = useLocalization();

    const { currentCustomTextProjectStore } = rootStore;
    const { projectKind } = currentCustomTextProjectStore;

    const isInEmptyState: boolean = !appliedFilters && !query && documents.length === 0;

    const [sortKey, setSortKey] = React.useState<SortKeyType<CustomTextDocumentMetadata>>({ key: 'name', order: 'asc' });

    const orderedDocuments = orderBy(documents, [createKeyedStringNormalizer(sortKey.key)], [sortKey.order]);

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(orderedDocuments, 10);

    const onSortColumn = (column: IColumn) => {
        setSortKey({ key: column.key as keyof CustomTextDocumentMetadata, order: column.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    return (
        <Stack grow tokens={{ childrenGap: 10 }} styles={containerStyles}>
            <AllFilesViewCommandBar
                query={query}
                isCommandBarDisabled={isInEmptyState}
                onQueryChange={setQuery}
                onChangeFilter={onChangeFilter}
                appliedFilters={appliedFilters}
                onFileViewChange={onFileViewChange}
                isExtractionProject={isExtractionProject}
            />

            {onRenderWarnings && onRenderWarnings()}

            {isInEmptyState ? (
                <EmptyStateView message={localize(messages.emptyStateMsg)} />
            ) : (
                <>
                    <Stack.Item grow>
                        <DocumentsTableWindow data-is-scrollable={!isAccessibleMode}>
                            <CtFiltersPillContainer appliedFilters={appliedFilters} onChangeFilter={onChangeFilter} maxDisplayNumber={4} />

                            <AllFilesViewTable
                                items={pageItems}
                                sortKey={sortKey}
                                selection={selection}
                                projectKind={projectKind}
                                onSortColumn={onSortColumn}
                                onDocumentClick={onDocumentClick}
                                isLoadingMoreFiles={isLoadingMoreFiles}
                                handleLoadMoreFiles={handleLoadMoreFiles}
                                showLoadMoreFiles={currentPage === pageCount - 1 && showLoadMoreFiles}
                            />
                        </DocumentsTableWindow>
                    </Stack.Item>

                    <TablePagination
                        pageCount={pageCount}
                        currentPage={currentPage}
                        onPageChange={p => {
                            selection.setAllSelected(false);
                            setCurrentPage(p.selected);
                        }}
                        containerStyles={paginationContainerStyles}
                    />
                </>
            )}
            <SamplingFooter onSave={onSave} savingState={savingState} areNavButtonsDisplayed={false} />
        </Stack>
    );
});
